import { ActivityPoint, AuthToken, Collection, ForkTreeNode, Library, LibraryDetail, LibrarySort, MapPost, PaginatedPrompts, PaginatedPublicCollections, PaginatedUsers, Prompt, PromptFormData, SuggestedUser, User, UserLocation } from '@/types';
import { getApiBaseUrl } from '@/lib/api-url';

const BASE_URL = getApiBaseUrl();
const TOKEN_KEY = 'prompt_gallery_token';

function getToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(TOKEN_KEY);
}

function setToken(token: string) {
  localStorage.setItem(TOKEN_KEY, token);
}

function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
}

async function request<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const token = getToken();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const res = await fetch(`${BASE_URL}${path}`, { ...options, headers });

  if (res.status === 401) {
    clearToken();
    if (typeof window !== 'undefined') window.location.href = '/auth/login';
    throw new Error('Unauthorized');
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(err.detail || 'Request failed');
  }

  if (res.status === 204) return undefined as T;
  return res.json();
}

export const api = {
  auth: {
    register: (data: { email: string; username: string; name: string; password: string; website?: string }, recaptchaToken?: string | null) =>
      request<AuthToken>('/auth/register', {
        method: 'POST',
        body: JSON.stringify(data),
        headers: recaptchaToken ? { 'x-recaptcha-token': recaptchaToken } : {},
      }),

    login: (email: string, password: string, recaptchaToken?: string | null) =>
      request<AuthToken>('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
        headers: recaptchaToken ? { 'x-recaptcha-token': recaptchaToken } : {},
      }),

    me: () => request<User>('/auth/me'),

    requestVerify: () =>
      request<{ sent?: boolean; already_verified?: boolean }>('/auth/verify/request', { method: 'POST' }),

    confirmVerify: (token: string) =>
      request<{ verified: boolean; username: string }>(
        '/auth/verify/confirm', { method: 'POST', body: JSON.stringify({ token }) },
      ),

    requestPasswordReset: (email: string) =>
      request<{ sent: true }>(
        '/auth/password-reset/request',
        { method: 'POST', body: JSON.stringify({ email }) },
      ),

    confirmPasswordReset: (token: string, new_password: string) =>
      // Backend returns {reset, access_token, user} — same auto-login shape
      // as register/login, just without token_type. Cast to AuthToken on
      // the client (token_type isn't read anywhere).
      request<{ reset: boolean; access_token: string; user: User }>(
        '/auth/password-reset/confirm',
        { method: 'POST', body: JSON.stringify({ token, new_password }) },
      ),
  },

  comments: {
    /**
     * Cursor-paginated. Pass `cursor` (returned as `next_cursor` from a prior
     * call) to fetch the next page; null/omit for the first page.
     */
    list: (promptId: string, opts?: { cursor?: string; limit?: number }) => {
      const qs = new URLSearchParams();
      if (opts?.cursor) qs.set('cursor', opts.cursor);
      if (opts?.limit)  qs.set('limit', String(opts.limit));
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      return request<{
        items: Array<{
          id: string;
          prompt_id: string;
          body: string;
          is_hidden: boolean;
          is_deleted: boolean;
          author: { id: string; username: string; name: string; avatar_url: string | null };
          created_at: string;
          updated_at: string;
        }>;
        next_cursor: string | null;
        total: number;
      }>(`/prompts/${promptId}/comments${suffix}`);
    },

    create: (promptId: string, body: string) =>
      request<{
        id: string;
        prompt_id: string;
        body: string;
        is_hidden: boolean;
        is_deleted: boolean;
        author: { id: string; username: string; name: string; avatar_url: string | null };
        created_at: string;
        updated_at: string;
      }>(`/prompts/${promptId}/comments`, { method: 'POST', body: JSON.stringify({ body }) }),

    edit: (id: string, body: string) =>
      request<{ id: string }>(`/comments/${id}`, { method: 'PATCH', body: JSON.stringify({ body }) }),

    remove: (id: string) =>
      request<void>(`/comments/${id}`, { method: 'DELETE' }),
  },

  reports: {
    file: (
      data: { target_type: 'prompt'; target_id: string; reason_kind: string; note?: string },
      recaptchaToken?: string | null,
    ) =>
      request<import('@/types').ReportOut>('/reports', {
        method: 'POST',
        body: JSON.stringify(data),
        headers: recaptchaToken ? { 'x-recaptcha-token': recaptchaToken } : {},
      }),
    my: () => request<import('@/types').ReportOut[]>('/reports/my'),
  },

  prompts: {
    list: (params: {
      search?: string;
      category?: string;
      tags?: string;
      /** Exact-match filter on the AI model slug (e.g. `openai/gpt-4o`). */
      model?: string;
      sort?: 'newest' | 'trending' | 'top_rated' | 'random';
      period?: 'day' | 'week' | 'month' | 'all';
      page?: number;
      size?: number;
    } = {}) => {
      const query = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== '') query.set(k, String(v));
      });
      return request<PaginatedPrompts>(`/prompts?${query.toString()}`);
    },

    get: (id: string) => request<Prompt>(`/prompts/${id}`),

    tree: (id: string) => request<{
      root: ForkTreeNode & { is_current: true };
      ancestors: ForkTreeNode[];
      descendants: (ForkTreeNode & { children: ForkTreeNode[] })[];
      truncated: boolean;
    }>(`/prompts/${id}/tree`),

    create: (data: PromptFormData) =>
      request<Prompt>('/prompts', { method: 'POST', body: JSON.stringify(data) }),

    update: (id: string, data: Partial<PromptFormData>) =>
      request<Prompt>(`/prompts/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

    delete: (id: string) =>
      request<void>(`/prompts/${id}`, { method: 'DELETE' }),

    save: (id: string) =>
      request<{ saved: boolean }>(`/prompts/${id}/save`, { method: 'POST' }),

    unsave: (id: string) =>
      request<{ saved: boolean }>(`/prompts/${id}/save`, { method: 'DELETE' }),

    copy: (id: string) =>
      request<{ copy_count: number; counted: boolean }>(`/prompts/${id}/copy`, { method: 'POST' }),
  },

  users: {
    profile: (username: string) =>
      request<{ user: User; prompts: Prompt[]; total_prompts: number }>(`/users/${username}`),

    myPrompts: () => request<PaginatedPrompts>('/users/me/prompts'),

    mySaved: () => request<PaginatedPrompts>('/users/me/saved'),

    updateMe: (payload: {
      name?: string;
      avatar_url?: string;
      cover_image_url?: string;
      bio?: string;
      social_links?: Record<string, string>;
      // Pass `null` to clear, an object to set, omit to leave unchanged.
      location?: UserLocation | null;
    }) => request<User>('/users/me', { method: 'PATCH', body: JSON.stringify(payload) }),

    // ── Social graph ─────────────────────────────────────────
    follow: (username: string) =>
      request<void>(`/users/${username}/follow`, { method: 'POST' }),

    unfollow: (username: string) =>
      request<void>(`/users/${username}/follow`, { method: 'DELETE' }),

    followers: (username: string, page = 1, size = 20) =>
      request<PaginatedUsers>(`/users/${username}/followers?page=${page}&size=${size}`),

    following: (username: string, page = 1, size = 20) =>
      request<PaginatedUsers>(`/users/${username}/following?page=${page}&size=${size}`),

    suggested: (limit = 10) =>
      request<SuggestedUser[]>(`/users/suggested?limit=${limit}`),

    feed: (params: { sort?: 'newest' | 'top_rated'; page?: number; size?: number } = {}) => {
      const q = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined) q.set(k, String(v));
      });
      return request<PaginatedPrompts>(`/users/me/feed?${q.toString()}`);
    },

    activity: (username: string, days = 365) =>
      request<{
        username: string;
        days: number;
        total: number;
        series: ActivityPoint[];
      }>(`/users/${username}/activity?days=${days}`),
  },

  tags: {
    list: () => request<{ name: string; count: number }[]>('/tags'),
  },

  map: {
    /**
     * Fetch map pins. Pass `bbox` as `[minLng, minLat, maxLng, maxLat]` to
     * narrow the viewport — useful when there are >5000 prompts globally
     * and the unfiltered call would hit the backend hard cap. Without bbox,
     * the backend returns up to 5000 most recent.
     */
    posts: (opts?: { bbox?: [number, number, number, number]; limit?: number }) => {
      const qs = new URLSearchParams();
      if (opts?.bbox)  qs.set('bbox', opts.bbox.join(','));
      if (opts?.limit) qs.set('limit', String(opts.limit));
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      return request<MapPost[]>(`/map/posts${suffix}`);
    },
  },

  stats: {
    public: () => request<{
      prompts_shared: number;
      copies_this_month: number;
      creators: number;
      total_saves: number;
      total_views: number;
    }>('/stats'),

    categories: () => request<{ category: string; count: number }[]>('/stats/categories'),
  },

  libraries: {
    /** Public: list of enabled libraries (sidebar, explore). */
    list: () => request<Library[]>('/libraries'),

    /** Public: library detail with live counts. */
    get: (slug: string) => request<LibraryDetail>(`/libraries/${slug}`),

    /** Public: paginated prompts in a library. */
    prompts: (slug: string, params: { sort?: LibrarySort; page?: number; size?: number } = {}) => {
      const qs = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => { if (v !== undefined) qs.set(k, String(v)); });
      return request<PaginatedPrompts>(`/libraries/${slug}/prompts?${qs.toString()}`);
    },

    /** Public: top contributors in the library. */
    experts: (slug: string, limit = 6) =>
      request<User[]>(`/libraries/${slug}/experts?limit=${limit}`),

    // Admin-scoped library CRUD (adminListAll / create / update / delete)
    // has moved to `adminApi.libraries.*` in `lib/admin-api.ts`. Those
    // endpoints now require a type=admin JWT.
  },

  collections: {
    list: () => request<Collection[]>('/collections'),

    public: (params: { search?: string; sort?: 'newest' | 'largest'; page?: number; size?: number } = {}) => {
      const qs = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== '') qs.set(k, String(v)); });
      return request<PaginatedPublicCollections>(`/collections/public?${qs.toString()}`);
    },
    create: (payload: { name: string; description?: string; hue?: string; icon?: string; is_public?: boolean }) =>
      request<Collection>('/collections', { method: 'POST', body: JSON.stringify(payload) }),
    get: (id: string) => request<Collection>(`/collections/${id}`),
    getPrompts: (id: string) => request<Prompt[]>(`/collections/${id}/prompts`),
    update: (id: string, payload: { name?: string; description?: string; hue?: string; icon?: string; is_public?: boolean }) =>
      request<Collection>(`/collections/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),
    delete: (id: string) => request<void>(`/collections/${id}`, { method: 'DELETE' }),
    addItem: (collectionId: string, promptId: string) =>
      request<{ added: boolean; already: boolean }>(`/collections/${collectionId}/items/${promptId}`, { method: 'POST' }),
    removeItem: (collectionId: string, promptId: string) =>
      request<void>(`/collections/${collectionId}/items/${promptId}`, { method: 'DELETE' }),
    byPrompt: (promptId: string) => request<string[]>(`/collections/by-prompt/${promptId}`),
    byUser: (username: string) => request<Collection[]>(`/users/${username}/collections`),
  },

  // NOTE: `api.admin.*` has moved to `src/lib/admin-api.ts` under its own
  // `adminApi` export + dedicated token key. Regular user-scoped calls never
  // authenticate against /admin anymore — those endpoints require type=admin
  // JWTs that this client doesn't hold.

  subscriptions: {
    /** Public list of active plans. Returns 404 when subscriptions are disabled site-wide. */
    plans: () => request<SubscriptionPlan[]>('/subscriptions/plans'),
    /** Current user's active subscription, or null. Returns 404 when disabled. */
    me: () => request<UserSubscription | null>('/subscriptions/me'),
  },

  translate: {
    /** Proxy to Google Translate (unofficial endpoint via the backend).
     *  Returns translated text + detected source language. */
    text: (text: string, target_lang: string, source_lang?: string) =>
      request<{ translated: string; source_lang: string; target_lang: string; truncated: boolean }>(
        '/translate',
        { method: 'POST', body: JSON.stringify({ text, target_lang, source_lang }) }
      ),
  },
};

// ── Subscription types (shared with admin-api) ──────────────────────
export type SubscriptionInterval = 'month' | 'year' | 'lifetime';

export interface SubscriptionPlan {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  price_cents: number;
  currency: string;
  interval: SubscriptionInterval;
  features: Record<string, unknown>;
  sort_order: number;
}

export interface UserSubscription {
  id: string;
  plan: SubscriptionPlan;
  status: 'active' | 'cancelled' | 'expired';
  started_at: string;
  current_period_end: string | null;
  is_currently_active: boolean;
}

export { getToken, setToken, clearToken };
